import React, { useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { Avatar, Drawer, Pagination } from "antd";
import { CloseOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import CustomButton from "../../../commonComponents/button";
import EmptyStateCard from "../../../common/EmptyStateCard";
import DebounceInput from "../../../commonComponents/DebounceInput";
import CustomSkeleton from "../../../commonComponents/CustomSkeleton";
import { SEARCH_LEADS_FOR_CO_BUYER } from "../../../../graphql/query";
import { displayPhone, displayEmail } from "../engagementHelpers";
import LeadInfoCard from "../../../common/LeadInfoCard";
import { COMMON_PAGINATION } from "../../../../library/constants";
import CoBuyerDetailDrawer from "./CoBuyerDetailDrawer";
import { useDevice } from "../../../../hooks/useDevice";

const AddCoBuyerDrawer = ({ open, onClose, onSave }) => {
  const { isMobile } = useDevice();
  const [search, setSearch] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(COMMON_PAGINATION.DEFAULT_PAGE_SIZE);
  const [showAddNewLead, setShowAddNewLead] = useState(false);

  const [searchLeads, { data, loading }] = useLazyQuery(SEARCH_LEADS_FOR_CO_BUYER, {
    fetchPolicy: "network-only",
  });

  const leads = data?.getLeads?.data || [];
  const totalCount = data?.getLeads?.count || 0;

  const handleSearch = value => {
    setSearch(value);
    setPage(1);
    if (value?.trim()) {
      searchLeads({
        variables: { search: value, page: 1, pageSize },
      });
    }
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    searchLeads({
      variables: { search, page: newPage, pageSize },
    });
  };

  const handleClose = () => {
    setSearch("");
    setSelectedLead(null);
    setPage(1);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      width={isMobile ? "100%" : 600}
      closable={false}
      title={
        <div className="flex items-center gap-2 min-w-0">
          <CloseOutlined className="cursor-pointer flex-shrink-0" onClick={handleClose} />
          <span className="truncate">Select lead (Add as co-buyer)</span>
        </div>
      }
      extra={
        <CustomButton
          type="primary"
          icon={<PlusOutlined />}
          btnText={isMobile ? "" : "Add new lead"}
          handleClick={() => setShowAddNewLead(true)}
        />
      }
      footer={
        <div className="flex items-center gap-2">
          <CustomButton
            type="primary"
            btnText="Save"
            handleClick={() => onSave?.(selectedLead)}
            disabled={!selectedLead}
          />
          <CustomButton btnText="Cancel" handleClick={handleClose} />
        </div>
      }
      styles={{ body: { display: "flex", flexDirection: "column", overflow: "hidden" } }}
    >
      {/* Sticky search bar */}
      <div className="flex-shrink-0">
        <DebounceInput
          placeholder="Search by name, phone or email"
          onChange={handleSearch}
          allowClear
        />
      </div>

      {/* Scrollable card container */}
      <div className="flex-1 overflow-y-auto mt-4">
        {!search?.trim() ? (
          <EmptyStateCard
            icon={
              <Avatar
                size={48}
                style={{ backgroundColor: "#f5f5f5" }}
                icon={<SearchOutlined style={{ fontSize: 20, color: "#8c8c8c" }} />}
              />
            }
            header="Search for a lead"
            subHeader="Search by name, phone or email to add as co-buyer"
            btnText="Add new lead"
            btnIcon={<PlusOutlined />}
            onBtnClick={() => setShowAddNewLead(true)}
          />
        ) : loading ? (
          <CustomSkeleton skeletonCount={8} skeletonHeight="3.5rem" skeletonWidth="34rem" />
        ) : leads.length === 0 ? (
          <EmptyStateCard
            header="No leads found"
            subHeader="Try a different search term"
            btnText="Add new lead"
            btnIcon={<PlusOutlined />}
            onBtnClick={() => setShowAddNewLead(true)}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {leads.map(lead => {
              const phone = displayPhone(lead);
              const email = displayEmail(lead);
              const subtitle = [phone !== "N/A" ? phone : "", email !== "N/A" ? email : ""]
                .filter(Boolean)
                .join(" • ");
              return (
                <LeadInfoCard
                  key={lead.id}
                  initials={`${lead.firstName?.[0] || ""}${lead.lastName?.[0] || ""}`}
                  title={`${lead.firstName || ""} ${lead.lastName || ""}`}
                  subtitle={subtitle}
                  isSelected={selectedLead?.id === lead.id}
                  onClick={() => setSelectedLead(selectedLead?.id === lead.id ? null : lead)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Sticky pagination */}
      {totalCount > COMMON_PAGINATION.DEFAULT_PAGE_SIZE && search?.trim() && !loading && (
        <div className="flex-shrink-0 flex justify-center pt-3">
          <Pagination
            size="small"
            current={page}
            pageSize={pageSize}
            total={totalCount}
            onChange={handlePageChange}
            onShowSizeChange={(_, size) => {
              setPageSize(size);
              setPage(1);
              searchLeads({ variables: { search, page: 1, pageSize: size } });
            }}
            showTotal={total => `Total: ${total}`}
            showSizeChanger
            pageSizeOptions={COMMON_PAGINATION.PAGE_SIZE_OPTIONS}
          />
        </div>
      )}
      {showAddNewLead && (
        <CoBuyerDetailDrawer
          open={showAddNewLead}
          onClose={() => setShowAddNewLead(false)}
          isEdit
          onSave={values => {
            setShowAddNewLead(false);
            onSave?.(values);
          }}
        />
      )}
    </Drawer>
  );
};

export default AddCoBuyerDrawer;
